
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TeacherMonthlyReport, ReportDetail } from '@/types/salary';
import { UseMutationResult } from '@tanstack/react-query';
import { TeacherSalaryFilters } from './TeacherSalaryFilters';
import { SortableTableHead } from './SortableTableHead';
import { useTeacherSalaryFilters } from '@/hooks/useTeacherSalaryFilters';

interface TeacherSalaryTableProps {
  teacherReports: TeacherMonthlyReport[];
  onViewDetails: (teacherId: string) => void;
  calculateSingleTeacherReportMutation: UseMutationResult<any, Error, string>;
}

export function TeacherSalaryTable({ 
  teacherReports, 
  onViewDetails,
  calculateSingleTeacherReportMutation 
}: TeacherSalaryTableProps) {
  const {
    searchQuery,
    setSearchQuery,
    confirmationStatusFilter,
    setConfirmationStatusFilter,
    sortField,
    sortDirection,
    filteredAndSortedReports,
    handleSort,
    clearFilters
  } = useTeacherSalaryFilters(teacherReports);

  // Filter teachers to only show those with income > 0
  const teachersWithIncome = teacherReports.filter(report => report.total_salary > 0);
  const confirmedTeachers = teachersWithIncome.filter(report => 
    report.confirmation_status === 'ACCEPTED'
  );

  const getConfirmationStatusBadge = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return <span className="text-green-600 font-medium">✓ ยืนยันแล้ว</span>;
      case 'DECLINED':
        return <span className="text-red-600 font-medium">✗ ปฏิเสธ</span>;
      case 'WAITING':
      default:
        return <span className="text-yellow-600 font-medium">⏳ รอยืนยัน</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Statistics */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{teachersWithIncome.length}</p>
            <p className="text-sm text-muted-foreground">ครูทั้งหมดที่มีรายได้</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{confirmedTeachers.length}</p>
            <p className="text-sm text-muted-foreground">ครูที่ยืนยันแล้ว</p>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <TeacherSalaryFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        confirmationStatusFilter={confirmationStatusFilter}
        onConfirmationStatusFilterChange={setConfirmationStatusFilter}
        onClearFilters={clearFilters}
      />

      {/* Teacher Salary Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-center">ลำดับ</TableHead>
              <SortableTableHead
                field="teacher_id"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                รหัสครู
              </SortableTableHead>
              <SortableTableHead
                field="teacher_name"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                ชื่อครู
              </SortableTableHead>
              <SortableTableHead
                field="total_reports"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-right"
              >
                จำนวนรายงาน
              </SortableTableHead>
              <SortableTableHead
                field="approved_worksheet_reports"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-right"
              >
                รายงานที่ Approved
              </SortableTableHead>
              <TableHead className="text-center">ดูรายละเอียด</TableHead>
              <SortableTableHead
                field="confirmation_status"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-center"
              >
                สถานะยืนยัน
              </SortableTableHead>
              <SortableTableHead
                field="total_salary"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-right"
              >
                รายได้ (บาท)
              </SortableTableHead>
              <SortableTableHead
                field="commission_amount"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-right"
              >
                ค่าคอม (บาท)
              </SortableTableHead>
              <SortableTableHead
                field="other_amount"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-right"
              >
                ค่าทดลองเรียน (บาท)
              </SortableTableHead>
              <TableHead className="text-right">
                รวม (บาท)
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedReports.map((report, index) => (
              <TableRow key={report.teacher_id}>
                <TableCell className="text-center">{index + 1}</TableCell>
                <TableCell>{report.teacher_id}</TableCell>
                <TableCell>{report.teacher_name || 'ไม่ระบุ'}</TableCell>
                <TableCell className="text-right">{report.total_reports}</TableCell>
                <TableCell className="text-right">{report.approved_worksheet_reports}</TableCell>
                <TableCell className="text-center">
                  <div className="flex justify-center space-x-2">
                    <Button 
                      variant="link" 
                      className="text-blue-500 text-center underline"
                      onClick={() => onViewDetails(report.teacher_id)}
                    >
                      ดูรายละเอียด
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => calculateSingleTeacherReportMutation.mutate(report.teacher_id)}
                      disabled={calculateSingleTeacherReportMutation.isPending}
                    >
                      คำนวณใหม่
                    </Button>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {getConfirmationStatusBadge(report.confirmation_status)}
                </TableCell>
                <TableCell className="text-right">
                  {report.total_salary.toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(report.commission_amount || 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(report.other_amount || 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {((report.commission_amount || 0) + (report.other_amount || 0) + report.total_salary).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            {filteredAndSortedReports.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  ไม่พบข้อมูลครูที่ตรงกับเงื่อนไขการค้นหา
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
